<script setup lang="ts">
/**
 * On Board — 반복부(인물 행) 리스트 에디터.
 *   `${prefix}_count` + 행별 `${prefix}_${i}_${col.key}` 로 저장.
 *   normalize 가 이 키들을 배열(loop)로 flatten 한다.
 *   화면은 원본 표(라벨 헤더 + 행)를 재현하고 각 셀은 CddVarField.
 */
import { computed } from 'vue';
import CddVarField from '../cdd/CddVarField.vue';
import { useAgmFill, DOC_SCOPE } from '../cdd/useCddFill';
import { inject } from 'vue';

const props = withDefaults(
  defineProps<{
    prefix: string;
    columns: Array<{ key: string; label: string }>;
    addLabel?: string;
    emptyNote?: string;
    showAdd?: boolean;
    showRemove?: boolean;
    /** 행이 0개면 안내 문구 없이 표를 숨긴다(추가 버튼은 유지). */
    hideEmpty?: boolean;
  }>(),
  { addLabel: '+ Add', emptyNote: '', showAdd: true, showRemove: true, hideEmpty: false },
);

const fill = useAgmFill();
const scope = inject(DOC_SCOPE, '');
const countKey = computed(() => scope + `${props.prefix}_count`);
const count = computed(() => {
  const n = parseInt(String(fill.getValue(countKey.value) || '0'), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
});
const rows = computed(() => Array.from({ length: count.value }, (_, i) => i + 1));

function add() {
  fill.setValue(countKey.value, String(count.value + 1));
}
function removeRow(i: number) {
  // i 이후 값을 한 칸씩 당겨서 재정렬
  const n = count.value;
  for (let j = i; j < n; j++) {
    for (const c of props.columns) {
      const from = scope + `${props.prefix}_${j + 1}_${c.key}`;
      const to = scope + `${props.prefix}_${j}_${c.key}`;
      fill.setValue(to, fill.getValue(from));
    }
  }
  for (const c of props.columns) fill.setValue(scope + `${props.prefix}_${n}_${c.key}`, '');
  fill.setValue(countKey.value, String(Math.max(0, n - 1)));
}
</script>

<template>
  <div class="loop-block">
    <div v-if="count === 0 && !hideEmpty" class="doc-note">{{ emptyNote || 'None.' }}</div>
    <table v-else class="cdd-tbl">
      <tbody>
        <tr v-for="i in rows" :key="i">
          <td v-for="c in columns" :key="c.key">
            <span class="loop-lbl">{{ c.label }}:</span>
            <CddVarField :field-key="`${prefix}_${i}_${c.key}`" :label="c.label" compact />
          </td>
          <td v-if="showRemove" class="loop-rm-cell">
            <button type="button" class="list-rm" title="Remove" @click="removeRow(i)">×</button>
          </td>
        </tr>
      </tbody>
    </table>
    <button v-if="showAdd" type="button" class="list-add" @click="add">{{ addLabel }}</button>
  </div>
</template>

<style scoped>
.loop-block { margin: 6px 0; }
.loop-lbl { color: #64748b; font-size: 0.82em; margin-right: 4px; }
.loop-rm-cell { width: 24px; text-align: center; }
.list-rm {
  border: none; background: #fdecec; color: #d14343; width: 20px; height: 20px;
  border-radius: 50%; cursor: pointer; font-size: 13px; line-height: 1;
}
.list-add {
  margin-top: 4px; border: 1px dashed #93b4e6; background: #f3f8ff; color: #1b6fd6;
  border-radius: 6px; padding: 3px 10px; font-size: 0.85em; cursor: pointer;
}
.list-add:hover { background: #e7f1ff; }
</style>
